import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { AlertTriangle, Mail, MapPin, User, Clock, Loader2 } from "lucide-react";

/** Public /grievance page — required for intermediaries under IT Rules.
 *  Displays Grievance Officer details and a public complaint form. */
export default function Grievance() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [ticketId, setTicketId] = useState(null);

  useEffect(() => {
    api.get("/platform-config").then((r) => setCfg(r.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.subject.trim() || !form.message.trim()) {
      toast.error("Please complete name, email, subject and message.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/grievance", form);
      setTicketId(data.ticket_id);
      toast.success("Grievance received. You will hear from us within 24 hours.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit — please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10 sm:py-14" data-testid="grievance-page">
      <div className="flex items-center gap-2 mb-3">
        <AlertTriangle className="h-5 w-5 text-primary" />
        <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Grievance Redressal · IT Rules 2021</span>
      </div>
      <h1 className="font-display font-extrabold text-3xl sm:text-4xl">Grievance Officer</h1>
      <p className="text-muted-foreground mt-3 max-w-2xl">
        In accordance with the Information Technology Act, 2000 and the Intermediary Guidelines, Jeweltix designates a Grievance Officer to receive and act on user complaints.
      </p>

      <div className="grid md:grid-cols-2 gap-6 mt-8">
        <div className="border border-border rounded-lg p-6 bg-card">
          <h2 className="font-display font-bold text-lg mb-4">Officer details</h2>
          {!cfg && <div className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>}
          {cfg && (
            <ul className="space-y-3 text-sm">
              <li className="flex gap-3"><User className="h-4 w-4 text-primary shrink-0 mt-0.5" /><span><strong className="font-mono text-xs uppercase tracking-wider text-muted-foreground block">Name</strong>{cfg.grievance_officer_name}</span></li>
              <li className="flex gap-3"><Mail className="h-4 w-4 text-primary shrink-0 mt-0.5" /><span><strong className="font-mono text-xs uppercase tracking-wider text-muted-foreground block">Email</strong><a href={`mailto:${cfg.grievance_officer_email}`} className="text-primary">{cfg.grievance_officer_email}</a></span></li>
              <li className="flex gap-3"><MapPin className="h-4 w-4 text-primary shrink-0 mt-0.5" /><span><strong className="font-mono text-xs uppercase tracking-wider text-muted-foreground block">Address</strong>{cfg.registered_address}</span></li>
              <li className="flex gap-3"><Clock className="h-4 w-4 text-primary shrink-0 mt-0.5" /><span><strong className="font-mono text-xs uppercase tracking-wider text-muted-foreground block">Response time</strong>Acknowledgement within 24 hours · Resolution within 15 days</span></li>
            </ul>
          )}
        </div>

        <form onSubmit={submit} className="border border-border rounded-lg p-6 bg-card" data-testid="grievance-form">
          <h2 className="font-display font-bold text-lg mb-4">Submit a complaint</h2>
          {ticketId && (
            <div className="mb-4 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900" data-testid="grievance-ticket">
              Complaint received. Your reference: <span className="font-mono">{ticketId.slice(0, 8)}</span>
            </div>
          )}
          <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="grievance-name" />
          <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="grievance-email" />
          <Field label="Phone (optional)" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="grievance-phone" />
          <Field label="Subject" value={form.subject} onChange={(v) => setForm({ ...form, subject: v })} testid="grievance-subject" />
          <label className="block mt-4">
            <span className="text-sm font-medium">Message</span>
            <textarea
              rows={5}
              required
              value={form.message}
              data-testid="grievance-message"
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="mt-1 w-full bg-card border border-border rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-primary resize-y"
            />
          </label>
          <button
            disabled={busy}
            data-testid="grievance-submit"
            className="mt-6 w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-md py-2.5 font-semibold hover:bg-primary/90 disabled:opacity-60"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Submit complaint
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <label className="block mt-4">
      <span className="text-sm font-medium">{label}</span>
      <input
        type={type}
        required={label !== "Phone (optional)"}
        value={value}
        data-testid={testid}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full bg-card border border-border rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-primary"
      />
    </label>
  );
}
